"use client";

import LocaleLink from "@/component/layout/LocaleLink";
import { ABOUT, ABOUT_CLOSING } from "@/data/about-content";
import { useContent } from "@/hooks/useContent";
import type { Bilingual, Locale } from "@/types/home";
import { motion } from "framer-motion";
import { useLocale } from "next-intl";
import AboutGroup from "./AboutGroup";
import AboutLeadership from "./AboutLeadership";
import AboutPartners from "./AboutPartners";
import AboutStory from "./AboutStory";
import AboutSubNav from "./AboutSubNav";

interface AboutContent {
  heroTitle: Bilingual;
  closing: { title: Bilingual; lead: Bilingual; primary: Bilingual; secondary: Bilingual };
}

/** About page — cinematic hero, sticky section nav, then the four chapters. */
export default function AboutParent() {
  const locale = useLocale() as Locale;
  const { data } = useContent<AboutContent>("about");
  const heroTitle = data?.heroTitle?.[locale] ?? ABOUT.title[locale];
  const closing = data?.closing;

  return (
    <div className="bg-white text-ash dark:bg-ash dark:text-white">
      {/* Hero */}
      <section className="relative flex h-[72vh] min-h-[440px] w-full items-end overflow-hidden bg-black">
        <motion.div
          initial={{ scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
          className="absolute inset-0 bg-cover bg-center opacity-45"
          style={{ backgroundImage: `url('${ABOUT.image}')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/30" />
        <div className="relative mx-auto w-full max-w-360 px-6 pb-14 lg:px-10">
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-[11px] uppercase tracking-[0.28em] text-accent"
          >
            {ABOUT.eyebrow[locale]}
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="mt-4 max-w-4xl font-bebas text-[clamp(2.4rem,6.5vw,6rem)] uppercase leading-[0.92] text-white"
          >
            {heroTitle}
          </motion.h1>
        </div>
      </section>

      <AboutSubNav />

      <AboutStory />
      <AboutGroup />
      <AboutLeadership />
      <AboutPartners />

      {/* Closing */}
      <section className="relative overflow-hidden bg-accent px-6 py-24 text-center text-white md:py-32">
        <span className="pointer-events-none absolute inset-0 flex select-none items-center justify-center font-bebas text-[26vw] leading-none text-white/10">
          TAHAWER
        </span>
        <div className="relative">
          <h2 className="font-bebas text-[clamp(2.5rem,6vw,5rem)] uppercase leading-none">{closing?.title?.[locale] ?? ABOUT_CLOSING.title[locale]}</h2>
          <p className="mx-auto mt-4 max-w-md text-white/80">{closing?.lead?.[locale] ?? ABOUT_CLOSING.lead[locale]}</p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <LocaleLink
              href="/projects"
              className="inline-flex items-center bg-white px-9 py-4 text-[12px] font-semibold uppercase tracking-widest text-ash transition-transform hover:-translate-y-0.5"
            >
              {closing?.primary?.[locale] ?? ABOUT_CLOSING.primary[locale]}
            </LocaleLink>
            <LocaleLink
              href="/contact"
              className="inline-flex items-center border border-white/50 px-9 py-4 text-[12px] font-semibold uppercase tracking-widest text-white transition-colors hover:bg-white/10"
            >
              {closing?.secondary?.[locale] ?? ABOUT_CLOSING.secondary[locale]}
            </LocaleLink>
          </div>
        </div>
      </section>
    </div>
  );
}
